// Состояние раздела «Модели» в query-параметрах URL: рефреш сохраняет вид,
// пересланная ссылка открывает в точности тот же вид.
//
// Чистые функции (без DOM) — тестируются отдельно. Центр/зум карты живут в тех же
// query-параметрах, но ими владеет useMapView (map/mapView.ts) — здесь мы их НЕ
// трогаем: applyModelsState сливает свои ключи, не затирая lng/lat/zoom и прочие.

import { DEPTH_LEVELS } from '../api'
import type { ModelKind } from './ModelPicker'
import { parseFleet, type FleetSegment } from './fleetSegments'
import { DEFAULT_FRONT_UNIT, type FrontUnit } from '../maps/frontsMath'
import { writeOverlaysUrl, parseOverlaysUrl, OVERLAY_URL_KEYS } from '../context/contextUrl'
import { WAVE_STEP_DEFAULT } from '../map/waveContours'
import { parseIsothermSpread } from '../map/isothermFamily'
import { DEFAULT_RASTER as UPW_DEFAULT_RASTER } from './upwellingSelection'
import { DEFAULT_HSI } from './hsiSelection'

export interface ModelsState {
  model: ModelKind
  species: number | null
  depth: number
  date: string
  // ── Блок «Наложения» (общий для обоих разделов, пишет активная страница) ──
  fleet: FleetSegment[]
  /** Оверлеи (землетрясения/фаза луны) — общий стор «Наложения». */
  overlays: string[]
  /** Оверлей «Волнение» (изо-линии + окраска стрелок по высоте волны). */
  waves: boolean
  /** Шаг изолиний волнения, м (дефолт 0.5). */
  waveStep: number
  /** «Показать суда без категорий» при выбранном виде. */
  showUncat: boolean
  /** Выбор флагов «Ин.флота»; пусто = все флаги. */
  flags: string[]
  /** Шаг изобат, м; null = раздел «Изобаты» выключен. */
  isobath: number | null
  /** Инструменты температуры на карте течений (как в «Картах»). */
  isotherm: number | null // градус активной изотермы; null = выкл
  /** Градус поверхностной изотермы (0 м); null = выкл. Дочерняя к `isotherm`. */
  isothermSurface: number | null
  /** Шаг смежных изотерм, °C; null = выкл. Тоже дочерний. */
  isothermSpread: number | null
  fronts: number | null // порог градиента (>0) в выбранной единице; null = выкл
  frontsUnit: FrontUnit // '100km' | '10km'
  /** «Траектории по HSI» на карте течений (отдельный слой, снос косяков не трогает). */
  hsiDrift: boolean
  /** Показывать вручную отмеченные косяки — и отметки, и их снос. */
  schools: boolean
  /** Прогнозный ход выбранного судна на сутки вперёд. */
  vesselForecast: boolean
  /** Двадцать мест нейрокарты поверх заливки. */
  nnMarks: boolean
  /** Исторический вылов (Ф58) в «Комфортной среде». */
  catchShown: boolean
  /** Каким подразделом закрашена «Комфортная среда»: `probability` — сезонная
   *  многопараметрическая модель, `nn` — карта нейросети. Ключ СЛОЯ, а не
   *  флажок: подразделов станет больше первым же обученным видом. */
  hsi: string
  /** Чем закрашено поле в разделе «Апвеллинг» (ключ механизма). */
  upw: string
  /** Какие механизмы наложены изолиниями. */
  upwIso: string[]
  /** Рисовать ли нулевую линию — границу подъёма и опускания. */
  upwZero: boolean
}

/** Дефолт раздела «Модели» (решение владельца 2026-08-03): открываем «По течениям»
 *  (дрейф), а не HSI-по-видам. Вид и флот сида ниже — в modelsSectionSeed. */
export const MODELS_DEFAULT_SPECIES = 26 // сайра (species_id=26)
export const MODELS_DEFAULT_FLEET: FleetSegment[] = ['foreign'] // Ин.флот

/** Сид раздела «Модели» для холодного входа без явных ключей: течения открываются
 *  на сайре и живых позициях иностранного флота. Возвращает ТОЛЬКО те значения,
 *  которых нет в URL, — явный вид/флот из ссылки (в т.ч. fleet=off) не переопределяем.
 *  Дату НЕ сидируем: она остаётся «сегодня»/последней доступной из общего контекста. */
export function modelsSectionSeed(
  params: URLSearchParams,
  brandSpecies?: number | null,
): { species?: number; fleet?: FleetSegment[] } {
  const seed: { species?: number; fleet?: FleetSegment[] } = {}
  // Вид домена (`default_species` пака) главнее умолчания раздела: сайры нет в
  // Аравийском море, и право на район сняло бы её, оставив кабинет без вида.
  if (!params.has('species')) seed.species = brandSpecies ?? MODELS_DEFAULT_SPECIES
  if (params.get('fleet') == null) seed.fleet = [...MODELS_DEFAULT_FLEET]
  return seed
}

export const DEFAULT_MODELS_STATE: ModelsState = {
  model: 'currents',
  species: null,
  // Раздел «Модели» открывается на 30 м по умолчанию (решение владельца
  // 2026-07-25, для всех моделей). 0 м теперь пишется в URL явно.
  depth: 30,
  date: '',
  fleet: [],
  overlays: [],
  waves: false,
  waveStep: WAVE_STEP_DEFAULT,
  showUncat: false,
  flags: [],
  isobath: null,
  isotherm: null,
  isothermSurface: null,
  isothermSpread: null,
  fronts: null,
  frontsUnit: DEFAULT_FRONT_UNIT,
  hsiDrift: false,
  // Единственный ключ раздела, включённый по умолчанию: косяки ставит сам
  // промысловик, и прятать его работу без спроса нельзя.
  schools: true,
  vesselForecast: false,
  // Места — товар нейрокарты, поэтому видны по умолчанию, как косяки.
  nnMarks: true,
  catchShown: false,
  // «Комфортная среда» открывается НЫНЕШНИМ индексом: ссылки `?model=hsi`
  // разосланы задолго до появления подразделов и обязаны открываться тем же,
  // чем открывались.
  hsi: DEFAULT_HSI,
  // Раздел открывается на ЭТАЛОНЕ, а не на замороженном индексе: старая ссылка
  // `?model=upwelling` неотличима от свежего входа, и оставив дефолтом
  // замороженный индекс, мы оставили бы лицом раздела ровно то, что океанолог
  // назвал неверным.
  upw: UPW_DEFAULT_RASTER,
  upwIso: [],
  // Единственный уровень, чей смысл не зависит от величины, — потому и включён:
  // граница двух бледных тонов растра глазом не локализуется.
  upwZero: true,
}

/** Ключи, которыми владеет раздел «Модели» (для чистого слияния в URL).
 *  'drift' и 'window' — ключи упразднённых выключателя сноса и ползунка
 *  «Свежесть»: состояния за ними больше нет, но они остаются в списке, чтобы
 *  старые ссылки чистились от них при первой же записи URL.
 *
 *  Ключи «Наложений» берутся ОДНИМ списком из `OVERLAY_URL_KEYS`, а не
 *  переписываются здесь руками. Переписанные — разъехались: `wavestep` и `flags`
 *  в перечень не попали, и снять их из ссылки стало нечем. Дефолтный шаг в URL
 *  не пишется (чистая ссылка), поэтому возврат к 0,5 не затирал прежние 0,6 —
 *  ссылка вечно несла старое значение, и после перезагрузки страница честно его
 *  и открывала (замечено владельцем 2026-08-08). «Карты» этой беды не знали:
 *  они собирают строку с нуля, а не сливают с прежней. */
export const MODELS_KEYS = [
  'model', 'species', 'depth', 'date', 'drift', 'window', 'iso', 'isosurf',
  'isostep', 'front', 'frontu', 'hsidrift', 'isobath', 'schools', 'vfc',
  'hsi', 'upw', 'upwiso', 'upw0', 'nnm', 'catch',
  ...OVERLAY_URL_KEYS,
] as const

const MODEL_VALUES: readonly ModelKind[] = ['hsi', 'upwelling', 'currents']

/** Сериализация → URLSearchParams (только свои ключи). Дефолты пропускаем ради чистого URL. */
export function serializeModelsState(state: ModelsState): URLSearchParams {
  const p = new URLSearchParams()
  if (state.model !== DEFAULT_MODELS_STATE.model) p.set('model', state.model)
  if (state.species != null) p.set('species', String(state.species))
  if (state.depth !== DEFAULT_MODELS_STATE.depth) p.set('depth', String(state.depth))
  if (state.date) p.set('date', state.date)
  // Наложения (overlays/fleet/waves/showuncat) — общий сериализатор с «Картами».
  writeOverlaysUrl(p, {
    overlays: state.overlays,
    fleet: state.fleet,
    waves: state.waves,
    showUncat: state.showUncat,
    waveStep: state.waveStep,
    flags: state.flags,
  })
  // Изотерма: 0° — валидный градус (!= выключено), поэтому != null, а не truthy.
  if (state.isobath != null) p.set('isobath', String(state.isobath))
  if (state.isotherm != null) {
    p.set('iso', String(state.isotherm))
    // Дочерние — только при живой основной. Шаг пишем и на дефолте: «1 °C» и
    // «выключено» — разные вещи, в отличие от wavestep.
    if (state.isothermSurface != null) p.set('isosurf', String(state.isothermSurface))
    if (state.isothermSpread != null) p.set('isostep', String(state.isothermSpread))
  }
  // Механизм заливки пишется, только когда он НЕ эталонный. Отличать «не
  // выбирали» от «выбрали главный» здесь нечего: заливка показана всегда, и
  // выключенного состояния у неё нет. А контракт раздела «дефолт → пустая
  // ссылка» стоит дорого: именно на его нарушении сломался `wavestep` —
  // дефолтное значение, попавшее в URL, не затиралось при возврате, и страница
  // вечно открывалась со старым.
  // Подраздел пишется, только когда он НЕ нынешний индекс: контракт «дефолт →
  // чистая ссылка» стоит дорого, именно на его нарушении сломался `wavestep`.
  if (state.hsi && state.hsi !== DEFAULT_HSI) p.set('hsi', state.hsi)
  if (state.upw && state.upw !== UPW_DEFAULT_RASTER) p.set('upw', state.upw)
  // `?.` не небрежность: состояние приезжает и из сохранённых снимков раздела,
  // сделанных ДО появления семьи механизмов, — у них этого поля нет вовсе.
  if (state.upwIso?.length) p.set('upwiso', state.upwIso.join(','))
  // Снятая нулевая линия — единственное, что стоит записи: она включена по
  // умолчанию, и писать `upw0=1` значило бы засорять каждую ссылку.
  if (state.upwZero === false) p.set('upw0', '0')

  // Градиент: только > 0 (сила = перепад). Единицу пишем лишь для активного
  // градиента и только не-дефолтную (10 км).
  if (state.fronts != null && state.fronts > 0) {
    p.set('front', String(state.fronts))
    if (state.frontsUnit === '10km') p.set('frontu', '10km')
  }
  if (state.hsiDrift) p.set('hsidrift', '1')
  if (state.vesselForecast) p.set('vfc', '1')
  // Косяки включены по умолчанию, поэтому в URL уезжает ВЫКЛЮЧЕНИЕ: чистая
  // ссылка обязана открываться с ними.
  if (!state.schools) p.set('schools', '0')
  if (state.nnMarks === false) p.set('nnm', '0')
  if (state.catchShown) p.set('catch', '1')
  return p
}

/** Устойчивый парс: неизвестные/битые значения игнорируются (→ дефолт при слиянии). */
export function parseModelsState(params: URLSearchParams): Partial<ModelsState> {
  const out: Partial<ModelsState> = {}

  const model = params.get('model')
  // Легаси: 'forecast' слит с 'hsi' (один probability-слой) — старые ссылки/сейвы
  // открываем как HSI, а не роняем в дефолт.
  if (model === 'forecast') out.model = 'hsi'
  else if (model && MODEL_VALUES.includes(model as ModelKind)) out.model = model as ModelKind

  const species = params.get('species')
  if (species) {
    const n = Number(species)
    if (Number.isInteger(n) && n >= 0) out.species = n
  }

  const depth = params.get('depth')
  if (depth) {
    const n = Number(depth)
    if (DEPTH_LEVELS.includes(n)) out.depth = n
  }

  const date = params.get('date')
  if (date && /^\d{4}-\d{2}-\d{2}$/.test(date)) out.date = date

  // Флот: parseFleet различает «ключа нет» (null → не трогаем) и «мусор» ([] →
  // ключ есть, но пусто) — контракт сохраняем как был.
  const fleet = parseFleet(params.get('fleet'))
  if (fleet) out.fleet = fleet

  // Прочие наложения — общий парс. Присваиваем только включённые/непустые, чтобы
  // дефолты не «прилипали» к partial и слияние оставалось чистым.
  const ov = parseOverlaysUrl(params)
  if (ov.overlays.length) out.overlays = ov.overlays
  if (ov.waves) out.waves = true
  if (ov.showUncat) out.showUncat = true
  if (ov.waveStep !== WAVE_STEP_DEFAULT) out.waveStep = ov.waveStep
  if (ov.flags.length) out.flags = ov.flags

  // drift из старых ссылок сознательно НЕ читаем: снос в разделе «По течениям»
  // включён всегда, и drift=0 не должен открывать раздел без его содержимого.

  // window из старых ссылок сознательно НЕ читаем: ползунок «Свежесть» убран
  // 11.09.2026, окно наблюдений одно — семь суток до выбранного дня. Ключ
  // остаётся в MODELS_KEYS, чтобы старая ссылка чистилась при первой записи.

  const isobath = params.get('isobath')
  if (isobath) {
    const n = Number(isobath)
    if (Number.isFinite(n) && n > 0) out.isobath = n
  }

  const iso = params.get('iso')
  if (iso != null && iso !== '') {
    const n = Number(iso)
    if (Number.isFinite(n)) out.isotherm = n // 0° валиден
  }

  // Дочерние читаем только при живой основной: ссылка `?isosurf=12` без `iso`
  // дала бы линию, которую нечем выключить — тумблер живёт внутри её блока.
  if (out.isotherm != null) {
    const surf = params.get('isosurf')
    if (surf != null && surf !== '') {
      const n = Number(surf)
      if (Number.isFinite(n)) out.isothermSurface = n // 0° валиден и здесь
    }
    const spread = params.get('isostep')
    if (spread != null && spread !== '') {
      out.isothermSpread = parseIsothermSpread(Number(spread))
    }
  }

  const front = params.get('front')
  if (front != null && front !== '') {
    const n = Number(front)
    if (Number.isFinite(n) && n > 0) out.fronts = n
  }
  if (params.get('frontu') === '10km') out.frontsUnit = '10km'

  if (params.get('hsidrift') === '1') out.hsiDrift = true
  if (params.get('vfc') === '1') out.vesselForecast = true
  // Читаем только точное '0'. Любое другое значение — мусор, и дефолт (косяки
  // видны) для него безопаснее догадки.
  if (params.get('schools') === '0') out.schools = false
  if (params.get('nnm') === '0') out.nnMarks = false
  if (params.get('catch') === '1') out.catchShown = true

  // Механизмы: чужие ключи здесь НЕ отбрасываются — каталог слоёв приезжает
  // запросом, и до его приезда судить о том, что бывает, нечем. Приведение
  // делает `clampSelection`, когда каталог уже на руках.
  // Чужой ключ здесь НЕ отбрасывается по той же причине, что у механизмов:
  // каталог слоёв приезжает запросом, и до его приезда судить о том, что
  // бывает, нечем. Приведение делает `clampHsi`, когда каталог уже на руках.
  const hsi = params.get('hsi')
  if (hsi) out.hsi = hsi
  const upw = params.get('upw')
  if (upw) out.upw = upw
  const upwIso = params.get('upwiso')
  if (upwIso != null) {
    out.upwIso = upwIso.split(',').map(s => s.trim()).filter(Boolean)
  }
  if (params.get('upw0') === '0') out.upwZero = false

  return out
}

/** Дописывает ключи раздела к параметрам страницы, не затирая чужие (lng/lat/zoom и т.п.).
 *  Сначала снимаем свои прежние ключи (чтобы дефолт очищал URL), затем ставим актуальные. */
export function applyModelsState(prev: URLSearchParams, state: ModelsState): URLSearchParams {
  const out = new URLSearchParams(prev)
  for (const key of MODELS_KEYS) out.delete(key)
  serializeModelsState(state).forEach((value, key) => out.set(key, value))
  return out
}
